import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { saveAs } from 'file-saver';
import { TracerService } from '../../../services/tracer.service';
import { SessionTicket, TicketSkill } from '../../../models/ticket.model';
import { ticketToMarkdown, humaniseKey } from '../../../pipes/ticket-to-markdown';
import { ModalDialogDirective } from '../../../directives/modal-dialog.directive';

type PanelStep = 'input' | 'result';

@Component({
  selector: 'app-ticket-panel',
  imports: [NgClass, NgxSpinnerComponent, FormsModule, RouterLink, ModalDialogDirective],
  templateUrl: './ticket-panel.html',
  styleUrl: './ticket-panel.scss',
})
export class TicketPanel implements OnInit {
  @Input() sessionValue: string = '';
  @Input() loading = false;
  @Output() closePanel = new EventEmitter<void>();

  // State
  step: PanelStep = 'input';
  skills: TicketSkill[] = [];
  loadingSkills = false;
  skillsError: string | null = null;
  selectedSkillId: number | null = null;
  supportGuidance = '';
  generating = false;
  generationError: string | null = null;
  ticket: SessionTicket | null = null;
  copyFeedback = '';

  // GitHub issue integration
  githubConnected = false;
  creatingIssue = false;
  issueError: string | null = null;

  constructor(private tracerService: TracerService) {}

  ngOnInit(): void {
    this.fetchSkills();
    // Cheap status check so we only show "Create GH Issue" when the
    // instance is actually connected. Non-fatal on error.
    this.tracerService.getGithubStatus().subscribe({
      next: res => (this.githubConnected = res.github.connected),
      error: () => (this.githubConnected = false),
    });
  }

  createIssue(): void {
    if (!this.ticket) return;
    this.creatingIssue = true;
    this.issueError = null;
    this.tracerService.createGithubIssue(this.sessionValue, this.ticket.id).subscribe({
      next: res => {
        this.creatingIssue = false;
        this.ticket = res.ticket;
      },
      error: err => {
        this.creatingIssue = false;
        this.issueError = err?.error?.error || 'Failed to create GitHub issue.';
      },
    });
  }

  private fetchSkills(): void {
    this.loadingSkills = true;
    this.skillsError = null;
    this.tracerService.listSkills(true).subscribe({
      next: res => {
        this.skills = res.skills || [];
        if (this.skills.length > 0 && this.selectedSkillId === null) {
          this.selectedSkillId = this.skills[0].id;
        }
        this.loadingSkills = false;
      },
      error: err => {
        this.loadingSkills = false;
        this.skillsError =
          err?.status === 403
            ? 'Admin-only feature.'
            : err?.error?.error || 'Could not load skills.';
      },
    });
  }

  generate(force = false): void {
    if (this.selectedSkillId === null || !this.sessionValue) return;
    this.generating = true;
    this.generationError = null;
    this.tracerService
      .generateTicket(this.sessionValue, this.selectedSkillId, this.supportGuidance, force)
      .subscribe({
        next: res => {
          this.ticket = res.ticket;
          this.step = 'result';
          this.generating = false;
        },
        error: err => {
          this.generating = false;
          if (err?.status === 403) {
            this.generationError = 'Not authorised: AI Ticket is admin-only.';
          } else if (err?.status === 404) {
            this.generationError = err?.error?.error || 'Session or skill not found.';
          } else if (err?.status === 409) {
            this.generationError = 'Selected skill has been disabled. Pick another.';
          } else {
            this.generationError = err?.error?.error || 'Generation failed.';
          }
        },
      });
  }

  backToInput(): void {
    this.step = 'input';
    this.copyFeedback = '';
  }

  regenerate(): void {
    this.generate(true);
  }

  get markdown(): string {
    return this.ticket ? ticketToMarkdown(this.ticket.result) : '';
  }

  get activeSkill(): TicketSkill | undefined {
    return this.skills.find(s => s.id === this.selectedSkillId);
  }

  get orderedResultEntries(): [string, unknown][] {
    if (!this.ticket?.result) return [];
    return Object.entries(this.ticket.result);
  }

  humanise(key: string): string {
    return humaniseKey(key);
  }

  isString(v: unknown): v is string {
    return typeof v === 'string';
  }

  isStringArray(v: unknown): v is string[] {
    return (
      Array.isArray(v) &&
      v.every(x => typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean')
    );
  }

  asString(v: unknown): string {
    return typeof v === 'string' ? v : String(v);
  }

  asStringArray(v: unknown): string[] {
    return Array.isArray(v) ? v.map(x => String(x)) : [];
  }

  copyMarkdown(): void {
    const md = this.markdown;
    if (!md) return;
    navigator.clipboard
      .writeText(md)
      .then(() => {
        this.copyFeedback = 'Copied!';
        setTimeout(() => (this.copyFeedback = ''), 1500);
      })
      .catch(() => {
        this.copyFeedback = 'Copy failed';
        setTimeout(() => (this.copyFeedback = ''), 2500);
      });
  }

  downloadMarkdown(): void {
    const md = this.markdown;
    if (!md) return;
    const safe =
      (this.titleFromResult() || 'ticket')
        .toLowerCase()
        .replace(/[^a-z0-9\-_]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'ticket';
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    saveAs(blob, `${safe}.md`);
  }

  private titleFromResult(): string | undefined {
    const t = this.ticket?.result?.['title'];
    return typeof t === 'string' ? t : undefined;
  }

  severityClasses(sev: unknown): string {
    const s = typeof sev === 'string' ? sev.toLowerCase() : '';
    switch (s) {
      case 'critical':
        return 'bg-red-200 text-red-900';
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  // Helpers so the template can classify a value without `typeof` in templates.
  kindOf(v: unknown): 'string' | 'string-array' | 'other' {
    if (typeof v === 'string') return 'string';
    if (this.isStringArray(v)) return 'string-array';
    return 'other';
  }

  asOther(v: unknown): string {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }
}
